import React, { useMemo } from "react";
import { PianoKeyboard } from "../piano/PianoKeyboard";
import { PianoPracticePanel } from "../piano/PianoPracticePanel";
import {
  buildSongPianoFingering,
  buildSongPianoNoteStates,
  primarySongActiveMidi,
  resolveSongPianoActiveStartTime,
} from "../../lib/songPianoHighway";
import type { TimedMusicXmlNote } from "../../lib/songEngine";

export interface SongPianoHighwayProps {
  notes: TimedMusicXmlNote[];
  currentNoteIndex: number;
  /** Instantáneo musical activo (reproducción); -1 en cuenta / silencio. */
  activeStartTime?: number;
  isPlaying?: boolean;
  noteFeedback?: Record<number, "correct" | "wrong">;
  /** Vista previa del siguiente ataque (solo en reposo / aprender). */
  lookaheadGroups?: number;
  className?: string;
}

/**
 * Highway de piano para canciones: resalta solo el evento rítmico activo;
 * en reproducción no acumula notas pasadas ni lookahead excesivo.
 */
export const SongPianoHighway: React.FC<SongPianoHighwayProps> = ({
  notes,
  currentNoteIndex,
  activeStartTime = -1,
  isPlaying = false,
  noteFeedback = {},
  lookaheadGroups = 1,
  className = "",
}) => {
  const eventStartTime = useMemo(
    () => resolveSongPianoActiveStartTime(notes, activeStartTime, currentNoteIndex),
    [notes, activeStartTime, currentNoteIndex]
  );

  const previewGroups = isPlaying ? 0 : lookaheadGroups;

  const fingering = useMemo(
    () => buildSongPianoFingering(notes, eventStartTime, previewGroups),
    [notes, eventStartTime, previewGroups]
  );

  const noteStates = useMemo(
    () => buildSongPianoNoteStates(notes, eventStartTime, noteFeedback),
    [notes, eventStartTime, noteFeedback]
  );

  const activeMidi = useMemo(
    () => primarySongActiveMidi(notes, eventStartTime),
    [notes, eventStartTime]
  );

  const highlightMidis = useMemo(
    () => new Set(fingering.map((f) => f.midi)),
    [fingering]
  );

  return (
    <PianoPracticePanel className={`min-h-[140px] h-full ${className}`}>
      <PianoKeyboard
        fingering={fingering}
        activeMidi={activeMidi}
        highlightMidis={highlightMidis}
        noteStates={noteStates}
        showFingerRings={false}
        showDegreeFill={false}
        colorByPitch
        dimUnmapped={false}
        embedded
        showHandZones={false}
      />
    </PianoPracticePanel>
  );
};
